import React from 'react'
import { Platform } from 'react-native'

/**
 * Gluestack decides whether a popover fits below its trigger by measuring
 * against the **window**, and exposes no boundary prop to say otherwise. Inside
 * a modal that is the wrong question: the Create Task dialog's body stops well
 * above the fixed "* Required fields" footer, so a panel that "fits the
 * viewport" is drawn over the footer and past the card's bottom edge (AOS-993
 * item 3 — measured 496x20px of overlap on the Rescue Service task-type field,
 * where the container leaves 15px below the trigger and 519px above it).
 *
 * So the placement is decided here instead, against the surface that actually
 * clips: the trigger's nearest scrolling ancestor, or failing that the nearest
 * dialog. `maxHeight` is returned alongside because flipping is not enough on
 * its own — a panel opening downward into 200px of room must still be capped to
 * it rather than spilling.
 *
 * Web only. On native the panel is a full-screen sheet over a backdrop, which
 * has no such boundary, and `getBoundingClientRect` does not exist there.
 */

export type DropdownPlacement = 'bottom left' | 'top left'

export type DropdownBoundary = 'container' | 'viewport'

export interface DropdownPlacementMetrics {
    placement: DropdownPlacement
    maxHeight?: number
}

export interface Bounds {
    top: number
    bottom: number
}

/** Matches the `max-h-[240px]` the option list carries by default. */
export const PANEL_MAX_HEIGHT = 240

/** Below this there is not enough room to show a usable number of rows. */
export const MIN_USABLE_HEIGHT = 120

const GAP = 8

const DEFAULT: DropdownPlacementMetrics = { placement: 'bottom left' }

/**
 * The surface that clips the panel: the nearest scrolling ancestor, else the
 * nearest dialog, else the viewport. Nearest wins — a modal's scrolling body
 * ends above the dialog's own footer, and it is the body that matters.
 */
const viewportBounds = (): Bounds => ({ top: 0, bottom: window.innerHeight })

export const findBoundary = (trigger: HTMLElement): Bounds => {
    let node: HTMLElement | null = trigger.parentElement
    while (node) {
        const scrolls = node.scrollHeight > node.clientHeight + 1
        if (scrolls || node.getAttribute('role') === 'dialog') {
            const rect = node.getBoundingClientRect()
            return { top: rect.top, bottom: rect.bottom }
        }
        node = node.parentElement
    }
    return viewportBounds()
}

export const resolveDropdownPlacement = (
    trigger: Bounds,
    boundary: Bounds,
): DropdownPlacementMetrics => {
    const below = boundary.bottom - trigger.bottom - GAP
    const above = trigger.top - boundary.top - GAP
    const flip = below < MIN_USABLE_HEIGHT && above > below
    const available = Math.max(0, flip ? above : below)

    return {
        placement: flip ? 'top left' : 'bottom left',
        maxHeight: Math.min(PANEL_MAX_HEIGHT, available),
    }
}

export const useDropdownPlacement = (
    isOpen: boolean,
    triggerRef: React.MutableRefObject<unknown>,
    boundary: DropdownBoundary = 'container',
): DropdownPlacementMetrics => {
    const [metrics, setMetrics] = React.useState<DropdownPlacementMetrics>(DEFAULT)

    React.useEffect(() => {
        if (Platform.OS !== 'web' || !isOpen) {
            return
        }
        const trigger = triggerRef.current as HTMLElement | null
        if (!trigger?.getBoundingClientRect) {
            return
        }
        const rect = trigger.getBoundingClientRect()
        setMetrics(
            resolveDropdownPlacement(
                { top: rect.top, bottom: rect.bottom },
                boundary === 'viewport' ? viewportBounds() : findBoundary(trigger),
            ),
        )
    }, [isOpen, triggerRef, boundary])

    return isOpen ? metrics : DEFAULT
}
